/**
 * Campaign content registry — multi-chapter.
 * Chapters/stages are immutable content tables; progress lives in campaignProgress.
 */

import type {
  CampaignDefinition,
  ChapterDefinition,
  StageDefinition
} from '../combat/campaign/types'
import { CHAPTER_1, CHAPTER_1_STAGES } from './chapter1'
import { CHAPTER_2, CHAPTER_2_STAGES } from './chapter2'

export const MAIN_CAMPAIGN: CampaignDefinition = {
  id: 'campaign-main',
  displayName: 'Campaign',
  chapterIds: ['chapter-1', 'chapter-2']
}

export const CHAPTER_BY_ID: Record<string, ChapterDefinition> = {
  [CHAPTER_1.id]: CHAPTER_1,
  [CHAPTER_2.id]: CHAPTER_2
}

/** All authored stages across chapters. */
export const ALL_STAGES: StageDefinition[] = [...CHAPTER_1_STAGES, ...CHAPTER_2_STAGES]

export const STAGE_BY_ID: Record<string, StageDefinition> = Object.fromEntries(
  ALL_STAGES.map((s) => [s.id, s])
)

export function listCampaigns(): CampaignDefinition[] {
  return [MAIN_CAMPAIGN]
}

export function listChapters(campaignId: string = MAIN_CAMPAIGN.id): ChapterDefinition[] {
  const campaign = campaignId === MAIN_CAMPAIGN.id ? MAIN_CAMPAIGN : undefined
  if (!campaign) return []
  return campaign.chapterIds
    .map((id) => CHAPTER_BY_ID[id])
    .filter((c): c is ChapterDefinition => Boolean(c))
}

export function listChapterStages(chapterId: string): StageDefinition[] {
  const chapter = CHAPTER_BY_ID[chapterId]
  if (!chapter) return []
  return chapter.stageIds
    .map((id) => STAGE_BY_ID[id])
    .filter((s): s is StageDefinition => Boolean(s))
}

export function getChapterForStage(stageId: string): ChapterDefinition | undefined {
  const stage = STAGE_BY_ID[stageId]
  if (!stage) return undefined
  return CHAPTER_BY_ID[stage.chapterId]
}

/** Max stars for a chapter = stageCount × 4 difficulties. */
export function chapterMaxStars(chapterId: string): number {
  const chapter = CHAPTER_BY_ID[chapterId]
  if (!chapter) return 0
  return chapter.stageIds.length * 4
}

/** Campaign-wide max stars (all chapters). */
export function campaignMaxStars(campaignId: string = MAIN_CAMPAIGN.id): number {
  return listChapters(campaignId).reduce((sum, ch) => sum + chapterMaxStars(ch.id), 0)
}

export { CHAPTER_1, CHAPTER_1_STAGES, CHAPTER_2, CHAPTER_2_STAGES }
export {
  CHAPTER_3_DEV,
  CHAPTER_3_DEV_STAGES,
  STAGE3_SNOW_DEV_ENABLED
} from './chapter3SnowDev'
